import { Badge } from '../../../ui/Badge';
import { Markdown } from '../../../ui/Markdown';
import { Card, KeyValue } from '../../../ui/misc';
import type { ImplDetail } from './types';

export function EvidenceCard({ i }: { i: ImplDetail }) {
  const criteria = i.ticket.acceptanceCriteria;
  const ev = i.evidence;
  return (
    <Card
      title="Evidence"
      subtitle={
        ev
          ? `Revision ${ev.revision} · recorded ${new Date(ev.recordedAt).toLocaleString()} · supplied by the owner`
          : 'No evidence recorded yet.'
      }
    >
      {ev && (
        <>
          <Markdown>{ev.summary}</Markdown>
          <KeyValue
            items={[
              [
                'Source revision',
                <span key="s" className="mono">
                  {ev.sourceRevision}
                </span>,
              ],
              ['Branch', ev.branch ?? '—'],
              [
                'Pull request',
                ev.pullRequest ? (
                  <a href={ev.pullRequest} target="_blank" rel="noopener noreferrer nofollow">
                    {ev.pullRequest}
                  </a>
                ) : (
                  '—'
                ),
              ],
            ]}
          />
          <span className="small strong">Criteria results</span>
          <ul className="checklist">
            {criteria.map((c, idx) => {
              const r = ev.criteriaResults.find((x) => x.criterionIndex === idx);
              return (
                <li key={idx} style={{ flexDirection: 'column', gap: 2 }}>
                  <span>
                    {idx + 1}. {c}{' '}
                    {r ? (
                      <Badge
                        tone={r.outcome === 'met' ? 'success' : r.outcome === 'not_met' ? 'error' : 'default'}
                      >
                        {r.outcome.replace('_', ' ')}
                      </Badge>
                    ) : (
                      <Badge tone="warning">Missing</Badge>
                    )}
                  </span>
                  {r?.observation && <span className="small muted">{r.observation}</span>}
                </li>
              );
            })}
          </ul>
          <span className="small strong">Tests and checks</span>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Command</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {ev.tests.map((t) => (
                  <tr key={t.name}>
                    <td className="mono small">{t.name}</td>
                    <td className="mono small">{t.command}</td>
                    <td>
                      <Badge
                        tone={t.result === 'passed' ? 'success' : t.result === 'failed' ? 'error' : 'default'}
                      >
                        {t.result.replace('_', ' ')}
                      </Badge>
                      {i.exceptions.some((e) => e.testName === t.name && !e.revokedAt) && (
                        <Badge tone="info">Exception approved</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ev.knownLimitations && (
            <>
              <span className="small strong">Known limitations</span>
              <Markdown>{ev.knownLimitations}</Markdown>
            </>
          )}
          <span className="small strong">Deployment and verification considerations</span>
          <Markdown>{ev.deploymentConsiderations}</Markdown>
          <span className="small strong">Rollback considerations</span>
          <Markdown>{ev.rollbackConsiderations}</Markdown>
          {ev.risks && (
            <>
              <span className="small strong">Risks</span>
              <Markdown>{ev.risks}</Markdown>
            </>
          )}
        </>
      )}
    </Card>
  );
}
